import qs from 'qs'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import { useExperimentState } from 'experiments/hooks/useExperiment'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useTikTokPixelTrackEvent } from 'head/hooks/useTikTokPixelTrackEvent'
import { t } from 'localization'
import { logAddToCart } from 'mParticle/ecommerceMParticleEvents'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import ShipView from 'productTemplate/components/ProductPicker/ShipView'
import { useProductTemplateContext } from 'productTemplate/components/ProductTemplateContextProvider'
import { getProductSizeCategory } from 'productTemplate/utils/getProductSizeCategory'
import { Button } from 'shared/components/Button'
import SideSheet from 'shared/components/SideSheet'
import SideSheetModal from 'shared/components/SideSheetModal'
import Spinner from 'shared/components/Spinner'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { ProductQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors, fonts, media } from 'shared/lib'
import { trackSnapchatAddCart } from 'tracking/snapchatService'
import PricingInfoModal from './PricingInfoModal'
import { ShoppingRegionSelector } from './ShoppingRegionSelector'

const TP = 'productTemplate.components.ProductPicker.ProductSideSheet'

type TitleProps = {
  name: string
  category: string
  size?: string
  color?: string
}

const Title = ({ name, category, size, color }: TitleProps) => {
  const getSizeLabel = (category: string, size: string) => {
    switch (category.toLowerCase()) {
      case 'men':
      case 'women':
        return t(`${TP}.sideSheetMenOrWomenSizingLabel`, {
          defaultValue: "US {category}'s Size {size}",
          category: getProductSizeCategory(category),
          size,
        })
      default:
        return t(`${TP}.sideSheetSizingLabel`, {
          defaultValue: 'US {category} Size {size}',
          category: getProductSizeCategory(category),
          size,
        })
    }
  }

  return (
    <Header>
      <ProductName data-qa={ProductQA.SideSheetShoeName}>{name}</ProductName>
      <SizingLabel data-qa={ProductQA.SideSheetSizingLabel} color={color ?? colors.FC2_GREY}>
        {getSizeLabel(category, size)}
      </SizingLabel>
    </Header>
  )
}

const ProductSideSheet = () => {
  const regionSelectorPlacement: EXPERIMENT_NAME = 'web_fc_fully_landed_pricing_copy_placement_test'
  const enableExperiments = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  const enablePricingCopyPlacementExperiment = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_FULLY_LANDED_PRICING_COPY_EXPERIMENT,
  )
  const enableAllInclusivePricingCopyExperiment = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_ALL_INCLUSIVE_PRICING_COPY_EXPERIMENT,
  )
  const webFCAllInclusivePricingExperiment: EXPERIMENT_NAME = 'web_fc_all_inclusive_pricing_v2'

  const { currencyCode: selectedCurrency, country } = useShoppingRegionContext()
  const experimentState = useExperimentState()
  const { trackTikTokCheckoutAddCart } = useTikTokPixelTrackEvent()
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false)
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)
  const {
    activeProductModal,
    setActiveProductModal,
    orderInfo,
    productTemplate,
    selectedSize,
    newProductView,
  } = useProductTemplateContext()

  const allInclusivePricingCopyExperimentGroup =
    enableExperiments &&
    enableAllInclusivePricingCopyExperiment &&
    experimentState[webFCAllInclusivePricingExperiment]
      ? experimentState[webFCAllInclusivePricingExperiment].groupAssigned
      : 'control'

  const isAllInclusivePricing =
    allInclusivePricingCopyExperimentGroup !== 'control' && country === 'US'

  const checkoutLink = `/checkout?${qs.stringify(
    orderInfo ? (({ instantShip, ...rest }) => rest)(orderInfo) : orderInfo,
  )}`

  const getAllInclusivePricingLabel = (group: string) => {
    switch (group) {
      case 'variant1':
        return t(`${TP}.noHiddenFees`, 'No hidden fees')
      case 'variant2':
        return t(`${TP}.pricesIncludeAllFees`, 'Prices include all fees')
      default:
        return ''
    }
  }

  const openPricingModal = () => {
    sendTrackingEvent('PDP_BUY_PANE_PRICING_INFO_TAP', {
      product_template_slug: productTemplate.slug,
    })
    setIsPricingModalOpen(true)
  }

  const closePricingModal = () => {
    setIsPricingModalOpen(false)
  }

  const handleSideSheetClose = () => {
    setActiveProductModal()
    closePricingModal()
    sendTrackingEvent('PDP_BUY_PANE_CLOSE', {
      product_template_slug: productTemplate.slug,
    })
  }

  const handleCheckoutButtonClick = () => {
    try {
      setIsCheckoutLoading(true)
      window.sessionStorage.setItem('recentAdd', `/checkout?${qs.stringify(orderInfo)}`)
      sendTrackingEvent('CART_ADD', {
        is_apparel_product: productTemplate.productCategory !== 'shoes',
        product_template_slug: productTemplate.slug,
        size: selectedSize?.size.display!,
        price: orderInfo?.expectedPriceCents!,
        condition: orderInfo?.productType?.toLowerCase()!,
        currency_code: selectedCurrency,
        instantShip: !!orderInfo?.instantShip,
        lowest_price: selectedSize?.new?.lowestPriceCents?.value || null,
        fastest_price: selectedSize?.new?.instantShipLowestPriceCents?.value || null,
      })
      logAddToCart({
        name: productTemplate.name,
        sku: productTemplate.sku,
        brandName: productTemplate.brandName,
        priceCents: orderInfo?.expectedPriceCents!,
        condition: orderInfo?.productType?.toLowerCase(),
      })
      trackSnapchatAddCart({ sku: productTemplate.sku })
      trackTikTokCheckoutAddCart({
        contentCategory: productTemplate.brandName,
        contentId: productTemplate.sku,
        price: orderInfo?.expectedPriceCents,
        quantity: 1,
      })

      window.location.href = checkoutLink
    } catch (error) {
      setIsCheckoutLoading(false)
    }
  }

  useEffect(() => {
    const handlePageShow = (event) => {
      // Check if the page was loaded from the back/forward cache
      if (event.persisted) {
        window.location.reload()
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  return (
    <SideSheet.Responsive
      title={
        <Title
          name={productTemplate.name}
          category={productTemplate.sizeCategory}
          size={selectedSize?.size.display}
        />
      }
      isVisible={!!activeProductModal}
      onClose={handleSideSheetClose}
      isFooterBoxShadowVisible={true}
    >
      <SideSheet.ModalOverlay>
        <SideSheetModal isOpen={isPricingModalOpen} hasPadding={false}>
          <PricingInfoModal closePricingModal={closePricingModal} data-testid="pricingInfoModal" />
        </SideSheetModal>
        <SideSheetModal isOpen={isCheckoutLoading} hasPadding={false} noBorder>
          <Spinner threshold={0} showSpinner={true} />
        </SideSheetModal>
      </SideSheet.ModalOverlay>
      <SideSheet.Body>
        {enableExperiments &&
          enablePricingCopyPlacementExperiment &&
          experimentState[regionSelectorPlacement]?.groupAssigned === 'test' && (
            <StyledShoppingRegionSelector>
              <ShoppingRegionSelector />
            </StyledShoppingRegionSelector>
          )}
        <ShipView openPricingModal={openPricingModal} />
      </SideSheet.Body>
      {activeProductModal === 'new' && (
        <SideSheet.Footer>
          <CheckoutButtonPositioner $isAllInclusivePricing={isAllInclusivePricing}>
            <Button
              buttonType="primary2"
              disabled={!orderInfo || newProductView === 'pickup'}
              $fill
              onClick={handleCheckoutButtonClick}
              qaAttr={ProductQA.BuyNewCheckoutButton}
            >
              {t(`${TP}.checkout`, 'Check Out')}
            </Button>
            {isAllInclusivePricing && (
              <FeesWrapper>
                <FeesLabel>
                  {getAllInclusivePricingLabel(allInclusivePricingCopyExperimentGroup || '')}
                </FeesLabel>
                <FeesLabel>
                  {t(`${TP}.taxesAndShipping`, 'Taxes and shipping calculated at checkout')}
                </FeesLabel>
              </FeesWrapper>
            )}
          </CheckoutButtonPositioner>
        </SideSheet.Footer>
      )}
    </SideSheet.Responsive>
  )
}

export default ProductSideSheet

const StyledShoppingRegionSelector = styled.div`
  padding: 0 20px;
`

const Header = styled.div`
  padding: 0;
`
const ProductName = styled.div`
  ${fonts.SUBTITLE_1};
  text-transform: uppercase;
  margin-bottom: 5px;
  color: ${colors.FC2_BLACK};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`

const SizingLabel = styled.label`
  ${fonts.SUBTITLE_3};
  color: ${({ color }) => (color ? color : colors.FC2_GREY)};
  text-transform: capitalize;
`

const FeesWrapper = styled.div`
  text-align: center;
  padding: 16px 0 0;
  ${media.large`
    padding: 16px 76px 0;
  `}
`

const FeesLabel = styled.div`
  ${fonts.BODY_TEXT};
  font-weight: 400;
  line-height: 140%;
`

const CheckoutButtonPositioner = styled.div<{ $isAllInclusivePricing?: boolean }>`
  padding: 20px 20px 35px;
  ${media.large`
    padding: ${({ $isAllInclusivePricing }) =>
      $isAllInclusivePricing ? '20px 30px' : '20px 30px 35px'};
  `}
`
